export type StudyPageThemeId = 'dark' | 'light' | 'custom';

export interface StudyPageTheme {
  id: StudyPageThemeId;
  name: string;
  description: string;
  colors: StudyPageColors;
}

export interface StudyPageColors {
  background: string;
  cardBackground: string;
  cardBorder: string;
  cardHoverBackground: string;
  cardHoverBorder: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentMuted: string;
  accentSubtle: string;
  buttonBackground: string;
  buttonHover: string;
  buttonText: string;
  divider: string;
  footerText: string;
  headerBarBg: string;
}

export const DARK_THEME: StudyPageColors = {
  background: '#1a2218',
  cardBackground: 'rgba(255, 255, 255, 0.03)',
  cardBorder: 'rgba(138, 154, 91, 0.15)',
  cardHoverBackground: 'rgba(255, 255, 255, 0.05)',
  cardHoverBorder: 'rgba(138, 154, 91, 0.25)',
  textPrimary: '#f0ead6',
  textSecondary: '#c8c4b0',
  textMuted: '#a8a498',
  accent: '#8a9a5b',
  accentMuted: '#a8b87a',
  accentSubtle: 'rgba(138, 154, 91, 0.12)',
  buttonBackground: '#4a5a3a',
  buttonHover: '#5a6a4a',
  buttonText: '#e8e2d0',
  divider: 'rgba(138, 154, 91, 0.1)',
  footerText: 'rgba(138, 154, 91, 0.3)',
  headerBarBg: 'rgba(26, 34, 24, 0.8)',
};

export const LIGHT_THEME: StudyPageColors = {
  background: '#faf8f4',
  cardBackground: 'rgba(0, 0, 0, 0.02)',
  cardBorder: 'rgba(45, 55, 72, 0.12)',
  cardHoverBackground: 'rgba(0, 0, 0, 0.04)',
  cardHoverBorder: 'rgba(45, 55, 72, 0.22)',
  textPrimary: '#1a202c',
  textSecondary: '#4a5568',
  textMuted: '#718096',
  accent: '#2b6cb0',
  accentMuted: '#4299e1',
  accentSubtle: 'rgba(43, 108, 176, 0.08)',
  buttonBackground: '#2b6cb0',
  buttonHover: '#2c5282',
  buttonText: '#ffffff',
  divider: 'rgba(45, 55, 72, 0.1)',
  footerText: 'rgba(45, 55, 72, 0.3)',
  headerBarBg: 'rgba(250, 248, 244, 0.85)',
};

export const DEFAULT_CUSTOM_COLORS: StudyPageColors = {
  background: '#1c1f2e',
  cardBackground: 'rgba(255, 255, 255, 0.04)',
  cardBorder: 'rgba(168, 139, 93, 0.15)',
  cardHoverBackground: 'rgba(255, 255, 255, 0.06)',
  cardHoverBorder: 'rgba(168, 139, 93, 0.25)',
  textPrimary: '#f5f0e8',
  textSecondary: '#d4cfc2',
  textMuted: '#a8a498',
  accent: '#a88b5d',
  accentMuted: '#c9a96e',
  accentSubtle: 'rgba(168, 139, 93, 0.1)',
  buttonBackground: '#6b5838',
  buttonHover: '#7d6842',
  buttonText: '#f5f0e8',
  divider: 'rgba(168, 139, 93, 0.1)',
  footerText: 'rgba(168, 139, 93, 0.3)',
  headerBarBg: 'rgba(28, 31, 46, 0.85)',
};

export const PRESET_THEMES: StudyPageTheme[] = [
  {
    id: 'dark',
    name: 'Dark',
    description: 'Olive green with cream text',
    colors: DARK_THEME,
  },
  {
    id: 'light',
    name: 'Light',
    description: 'Clean ivory with navy accents',
    colors: LIGHT_THEME,
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'Your personalized colors',
    colors: DEFAULT_CUSTOM_COLORS,
  },
];

export interface CustomColorConfig {
  background: string;
  accent: string;
  textPrimary: string;
  cardBackground: string;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  } : null;
}

function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
}

function adjustBrightness(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  const r = clamp(rgb.r + amount);
  const g = clamp(rgb.g + amount);
  const b = clamp(rgb.b + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export function buildCustomTheme(config: CustomColorConfig): StudyPageColors {
  const light = isLightColor(config.background);
  const accentRgb = hexToRgb(config.accent);
  const accentR = accentRgb?.r ?? 138;
  const accentG = accentRgb?.g ?? 154;
  const accentB = accentRgb?.b ?? 91;

  return {
    background: config.background,
    cardBackground: config.cardBackground || (light ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)'),
    cardBorder: `rgba(${accentR}, ${accentG}, ${accentB}, 0.15)`,
    cardHoverBackground: light ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
    cardHoverBorder: `rgba(${accentR}, ${accentG}, ${accentB}, 0.25)`,
    textPrimary: config.textPrimary,
    textSecondary: light ? adjustBrightness(config.textPrimary, 60) : adjustBrightness(config.textPrimary, -40),
    textMuted: light ? adjustBrightness(config.textPrimary, 100) : adjustBrightness(config.textPrimary, -70),
    accent: config.accent,
    accentMuted: adjustBrightness(config.accent, light ? -30 : 30),
    accentSubtle: `rgba(${accentR}, ${accentG}, ${accentB}, ${light ? '0.08' : '0.12'})`,
    buttonBackground: light ? config.accent : adjustBrightness(config.accent, -30),
    buttonHover: light ? adjustBrightness(config.accent, -20) : adjustBrightness(config.accent, -15),
    buttonText: isLightColor(config.accent) ? '#1a1a1a' : '#ffffff',
    divider: `rgba(${accentR}, ${accentG}, ${accentB}, 0.1)`,
    footerText: `rgba(${accentR}, ${accentG}, ${accentB}, 0.3)`,
    headerBarBg: light ? `rgba(${hexToRgb(config.background)?.r ?? 250}, ${hexToRgb(config.background)?.g ?? 248}, ${hexToRgb(config.background)?.b ?? 244}, 0.85)` : `rgba(${hexToRgb(config.background)?.r ?? 26}, ${hexToRgb(config.background)?.g ?? 34}, ${hexToRgb(config.background)?.b ?? 24}, 0.8)`,
  };
}

export function getThemeColors(themeId: StudyPageThemeId, customColors?: CustomColorConfig | null): StudyPageColors {
  if (themeId === 'custom') {
    return buildCustomTheme(customColors || {
      background: DEFAULT_CUSTOM_COLORS.background,
      accent: DEFAULT_CUSTOM_COLORS.accent,
      textPrimary: DEFAULT_CUSTOM_COLORS.textPrimary,
      cardBackground: '',
    });
  }
  if (themeId === 'light') return LIGHT_THEME;
  return DARK_THEME;
}

export function loadCustomColors(): CustomColorConfig {
  try {
    const saved = localStorage.getItem('study_page_custom_colors');
    if (saved) return JSON.parse(saved);
  } catch {}
  return {
    background: DEFAULT_CUSTOM_COLORS.background,
    accent: DEFAULT_CUSTOM_COLORS.accent,
    textPrimary: DEFAULT_CUSTOM_COLORS.textPrimary,
    cardBackground: '',
  };
}

export function saveCustomColors(config: CustomColorConfig) {
  localStorage.setItem('study_page_custom_colors', JSON.stringify(config));
}

export function loadSelectedTheme(): StudyPageThemeId {
  try {
    const saved = localStorage.getItem('study_page_theme');
    if (saved === 'dark' || saved === 'light' || saved === 'custom') return saved;
  } catch {}
  return 'dark';
}

export function saveSelectedTheme(themeId: StudyPageThemeId) {
  localStorage.setItem('study_page_theme', themeId);
}
